import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Button, Typography, Spin, Card, Row, Col, Input } from 'antd';
import {
  apiGetAllPermissions,
  apiGetRolePermissions,
  apiAssignPermissionsToRole
} from '@services/ManageRoles/RoleServices';
import { showSuccess, showError } from '@components/notification';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Title, Text } = Typography;
const { Search } = Input;

const AssignPermissionsModal = ({ open, onClose, role }) => {
  const [allPermissions, setAllPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { isSuperAdmin } = usePermission();

  // Group permissions by resource
  const groupedPermissions = allPermissions.reduce((acc, permission) => {
    const resource = permission.resource || 'other';
    if (!acc[resource]) {
      acc[resource] = [];
    }
    acc[resource].push(permission);
    return acc;
  }, {});

  const filteredGroupedPermissions = Object.keys(groupedPermissions).reduce((acc, resource) => {
    const filteredPermissions = groupedPermissions[resource].filter(
      (permission) =>
        permission.name.toLowerCase().includes(searchText.toLowerCase()) ||
        permission.description.toLowerCase().includes(searchText.toLowerCase())
    );
    if (filteredPermissions.length > 0) {
      acc[resource] = filteredPermissions;
    }
    return acc;
  }, {});

  useEffect(() => {
    if (open && role) {
      fetchData();
      setSearchText(''); // Clear search text when modal opens
    }
  }, [open, role]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch all permissions and current role permissions
      const [allPermsResponse, rolePermsResponse] = await Promise.all([
        apiGetAllPermissions(),
        apiGetRolePermissions(role.id)
      ]);

      let allPerms = allPermsResponse?.data?.data || [];
      const rolePerms = rolePermsResponse?.data?.data || [];

      // Chỉ SUPER_ADMIN mới có thể phân quyền auth_manage
      if (!isSuperAdmin) {
        allPerms = allPerms.filter((perm) => perm.name !== 'auth_manage');
      }

      setAllPermissions(allPerms);
      setRolePermissions(rolePerms);

      // Set currently selected permissions
      const currentPermissionIds = rolePerms.map((p) => p.id);
      setSelectedPermissions(currentPermissionIds);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      showError('Có lỗi xảy ra khi tải danh sách quyền hạn');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permissionId, checked) => {
    setSelectedPermissions((prev) => {
      if (checked) {
        return [...prev, permissionId];
      } else {
        return prev.filter((id) => id !== permissionId);
      }
    });
  };

  const handleSelectAll = (resourcePermissions, checked) => {
    const resourcePermissionIds = resourcePermissions.map((p) => p.id);
    setSelectedPermissions((prev) => {
      if (checked) {
        // Add all permissions from this resource
        return [...new Set([...prev, ...resourcePermissionIds])];
      } else {
        // Remove all permissions from this resource
        return prev.filter((id) => !resourcePermissionIds.includes(id));
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiAssignPermissionsToRole({
        role_id: role.id,
        permission_ids: selectedPermissions
      });
      showSuccess({ message: 'Cập nhật quyền hạn thành công!' });
      onClose();
    } catch (error) {
      showError(error?.message || 'Không thể cập nhật quyền hạn');
    } finally {
      setSaving(false);
    }
  };

  const getResourceDisplayName = (resource) => {
    const resourceNames = {
      user: 'Quản lý người dùng',
      role: 'Quản lý vai trò',
      permission: 'Quản lý quyền hạn',
      report: 'Báo cáo',
      device: 'Thiết bị',
      customer: 'Khách hàng',
      config: 'Cấu hình',
      statistics: 'Thống kê',
      other: 'Tất cả'
    };
    return resourceNames[resource] || resource;
  };

  return (
    <Modal
      title={`Gán quyền hạn cho vai trò: ${role?.name}`}
      open={open}
      onCancel={onClose}
      width="80%"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Hủy
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
          style={{ backgroundColor: '#0284c7', borderColor: '#0284c7' }}>
          Lưu thay đổi
        </Button>
      ]}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Đang tải danh sách quyền hạn...</div>
        </div>
      ) : (
        <>
          <Search
            placeholder="Tìm kiếm quyền hạn..."
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {Object.keys(filteredGroupedPermissions).map((resource) => {
              const resourcePermissions = filteredGroupedPermissions[resource];
              const allSelected = resourcePermissions.every((p) =>
                selectedPermissions.includes(p.id)
              );
              const someSelected = resourcePermissions.some((p) =>
                selectedPermissions.includes(p.id)
              );

              return (
                <Card
                  key={resource}
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(e) => handleSelectAll(resourcePermissions, e.target.checked)}>
                      <Title level={5} style={{ margin: 0, display: 'inline' }}>
                        {getResourceDisplayName(resource)}
                      </Title>
                    </Checkbox>
                  }>
                  <Row gutter={[16, 8]}>
                    {resourcePermissions.map((permission) => (
                      <Col span={8} key={permission.id}>
                        <Checkbox
                          checked={selectedPermissions.includes(permission.id)}
                          onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}>
                          <div>
                            <Text strong>{permission.name}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {permission.description}
                            </Text>
                          </div>
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Card>
              );
            })}
          </div>

          {Object.keys(filteredGroupedPermissions).length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Text type="secondary">
                {searchText ? 'Không tìm thấy quyền hạn nào phù hợp' : 'Chưa có quyền hạn nào'}
              </Text>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default AssignPermissionsModal;
